import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserTrial {
  user_id: string;
  trial_started_at: string;
  trial_tier: string;
  trial_expired: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * IMPORTANTE: Este hook es SOLO para usuarios regulares.
 * Los proveedores de servicios tienen su propio sistema de suscripción
 * en service_providers table y NO deben usar el trial de 15 días.
 */
export const useTrialStatus = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is a service provider
  const { data: isProvider, isLoading: isProviderLoading } = useQuery({
    queryKey: ['is-provider', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase
        .from('service_providers')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking provider status:', error);
        return false;
      }

      return !!data;
    },
    enabled: !!user?.id,
  });

  // Fetch trial status - UNIFIED SYSTEM (reads from users.premium_trial_ends_at)
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user-trial', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('users')
        .select('premium_trial_ends_at')
        .eq('id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching trial:', error);
        throw error;
      }

      return data;
    },
    enabled: !!user?.id,
  });

  // Calculate trial status from premium_trial_ends_at
  // CRITICAL: Providers do NOT use trial system - they have their own subscription
  const isInTrial = isProvider 
    ? false  // Service providers are excluded from trial system
    : (userData?.premium_trial_ends_at 
        ? new Date(userData.premium_trial_ends_at).getTime() > Date.now()
        : false);

  const daysRemaining = userData?.premium_trial_ends_at && isInTrial
    ? Math.max(0, Math.ceil(
        (new Date(userData.premium_trial_ends_at).getTime() - Date.now()) / 
        (24 * 60 * 60 * 1000)
      ))
    : 0;

  const trialExpired = userData?.premium_trial_ends_at 
    ? new Date(userData.premium_trial_ends_at).getTime() <= Date.now()
    : false;

  // Start trial mutation - server-side via SECURITY DEFINER RPC
  const startTrial = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.rpc('start_user_trial');

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-trial'] });
      toast.success('🎉 ¡Tu trial de 15 días ha comenzado!', {
        description: 'Disfruta de todas las funciones premium gratis',
      });
    },
    onError: (error: any) => {
      console.error('Error starting trial:', error);
      toast.error('Error al iniciar trial', {
        description: error.message || 'Intenta nuevamente',
      });
    },
  });

  return {
    trial: userData?.premium_trial_ends_at && !isProvider ? {
      user_id: user?.id || '',
      trial_started_at: new Date(new Date(userData.premium_trial_ends_at).getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      trial_tier: 'premium',
      trial_expired: trialExpired,
      created_at: '',
      updated_at: ''
    } as UserTrial : null,
    isInTrial: !!isInTrial,
    daysRemaining,
    trialExpired,
    isLoading: isLoading || isProviderLoading,
    startTrial: startTrial.mutate,
    isStartingTrial: startTrial.isPending,
  };
};
